import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './context/AuthContext';
import { UserDataProvider } from './context/UserDataContext';
import { AccountDataProvider } from './context/AccountDataContext';
import { NotificationProvider } from './context/NotificationContext';
import { RationingProvider } from './context/RationingContext';
import { SiteRatesProvider } from './context/SiteRatesContext';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import PrepaidUserDashboard from './pages/user/PrepaidUserDashboard';
import PostpaidUserDashboard from './pages/user/PostpaidUserDashboard';
import UserDashboard from './pages/user/UserDashboard';
import Profile from './pages/Profile';
import ElectricityData from './pages/user/ElectricityData';
import Billing from './pages/user/Billing';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagementPage from './pages/admin/UserManagementPage';
import SiteManagementPage from './pages/admin/SiteManagementPage';
import BulkCreateUnitsPage from './pages/admin/BulkCreateUnitsPage';
import EndpointTest from './pages/admin/EndpointTest';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PendingApproval from './pages/PendingApproval';
import OperatorManagerRegister from './pages/OperatorManagerRegister';
import ProtectedRoute from './components/ProtectedRoute';
import ShiftChangeMonitor from './components/ShiftChangeMonitor';
import PaymentResultPage from './pages/payments/PaymentResultPage';
import RationingPage from './pages/user/RationingPage';
import YocoSettingsPage from './pages/admin/YocoSettingsPage';
import MonthlyFixedChargeWriteOffPage from './pages/admin/MonthlyFixedChargeWriteOffPage';

// Lazy load DesignSystem only in development
// This ensures it's not included in the production bundle at all
const DesignSystem = import.meta.env.DEV 
  ? lazy(() => import('./pages/DesignSystem')) 
  : () => null;

function App() {
  return (
    <SiteRatesProvider>
      <AuthProvider>
        <UserDataProvider>
          <AccountDataProvider>
            <NotificationProvider>
              <RationingProvider>
                <Router>
                  <ShiftChangeMonitor />
                  <Routes>
                    <Route path="/" element={<Layout />}>
                      {/* Public routes */}
                      <Route index element={<Home />} />
                      <Route path="login" element={<Login />} />
                      <Route path="register" element={<Register />} />
                      <Route path="forgot-password" element={<ForgotPassword />} />
                      <Route path="reset-password" element={<ResetPassword />} />
                      <Route path="register-internal" element={<OperatorManagerRegister />} />
                      <Route path="pending-approval" element={<PendingApproval />} />
                    
                      {/* Design system route - only available in development */}
                      {import.meta.env.DEV && (
                        <Route 
                          path="design-system" 
                          element={
                            <Suspense fallback={<div>Loading...</div>}>
                              <DesignSystem />
                            </Suspense>
                          } 
                        />
                      )}
                    
                      {/* Hosted checkout return routes - paths must match the
                          backend peach_settings for this site. The key prop
                          forces a remount if the SPA ever transitions between
                          outcome routes, so per-checkout state (countdown,
                          popped stash) never leaks across outcomes. */}
                      <Route path="payment/success" element={
                        <ProtectedRoute>
                          <PaymentResultPage key="success" outcome="success" />
                        </ProtectedRoute>
                      } />
                      <Route path="payment/pending" element={
                        <ProtectedRoute>
                          <PaymentResultPage key="pending" outcome="pending" />
                        </ProtectedRoute>
                      } />
                      <Route path="payment/failed" element={
                        <ProtectedRoute>
                          <PaymentResultPage key="failed" outcome="failed" />
                        </ProtectedRoute>
                      } />
                      {/* Yoco returns the shopper to the URLs stored in
                          yoco_settings, which predate the provider-neutral
                          routes above and point at /payment/cancel and
                          /payment/failure. Unrouted paths render a blank page,
                          so both are served here. A cancellation is shown as
                          "not completed" because nothing was charged - the same
                          reading the Yoco-era client used. */}
                      <Route path="payment/cancel" element={
                        <ProtectedRoute>
                          <PaymentResultPage key="cancel" outcome="failed" />
                        </ProtectedRoute>
                      } />
                      <Route path="payment/failure" element={
                        <ProtectedRoute>
                          <PaymentResultPage key="failure" outcome="failed" />
                        </ProtectedRoute>
                      } />

                      {/* Protected routes */}
                      <Route path="dashboard" element={
                        <ProtectedRoute>
                          <UserDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="dashboard/prepaid" element={
                        <ProtectedRoute>
                          <PrepaidUserDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="dashboard/postpaid" element={
                        <ProtectedRoute>
                          <PostpaidUserDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="profile" element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      } />
                      <Route path="electricity-data" element={
                        <ProtectedRoute>
                          <ElectricityData />
                        </ProtectedRoute>
                      } />
                      <Route path="billing" element={
                        <ProtectedRoute>
                          <Billing />
                        </ProtectedRoute>
                      } />
                      <Route path="rationing" element={<RationingPage />} />
                    
                      {/* Admin routes */}
                      <Route path="admin" element={
                        <ProtectedRoute requiredRoles={['ADMIN', 'MANAGEMENT', 'OPERATOR']}>
                          <AdminDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="user-management" element={
                        <ProtectedRoute requiredRoles={['ADMIN', 'MANAGEMENT', 'OPERATOR']}>
                          <UserManagementPage />
                        </ProtectedRoute>
                      } />
                      <Route path="site-management" element={
                        <ProtectedRoute requiredRoles={['ADMIN', 'MANAGEMENT']}>
                          <SiteManagementPage />
                        </ProtectedRoute>
                      } />
                      <Route path="site-management/bulk-create-units" element={
                        <ProtectedRoute requiredRoles={['ADMIN', 'MANAGEMENT']}>
                          <BulkCreateUnitsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="endpoint-test" element={
                        <ProtectedRoute requiredRoles={['ADMIN']}>
                          <EndpointTest />
                        </ProtectedRoute>
                      } />
                      <Route path="admin/yoco-settings" element={
                        <ProtectedRoute requiredRoles={['ADMIN']}>
                          <YocoSettingsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="admin/monthly-fixed-charge-write-offs" element={
                        <ProtectedRoute requiredRoles={['ADMIN']}>
                          <MonthlyFixedChargeWriteOffPage />
                        </ProtectedRoute>
                      } />

                      {/* Without this, any unmatched path renders an empty page
                          with no error - how a provider redirect to an unrouted
                          return URL surfaced as a blank screen. */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Routes>
                </Router>
              </RationingProvider>
            </NotificationProvider>
          </AccountDataProvider>
        </UserDataProvider>
      </AuthProvider>
    </SiteRatesProvider>
  );
}

export default App; 